import React from 'react';

function LocationSearchBar({
  value,
  onChange,
  onSearch,
  filteredResults = [],
  onSelectResult,
  placeholder = 'Search by city, state, or course...',
  loading = false,
  onFocus,
  onBlur,
  showDropdown,
  onCurrentLocation,
  variant = 'default' // 'default' for home page, 'compact' for map page
}) {
  const isDesktop = typeof window !== 'undefined' && window.innerWidth > 768;
  
  // Separate city and course results
  const cityResults = filteredResults.filter(result => result.type === 'city' || !result.type);
  const courseResults = filteredResults.filter(result => result.type === 'course');
  
  // Style based on variant
  const isCompact = variant === 'compact';
  const inputPadding = isCompact 
    ? '8px 36px 8px 12px' 
    : (isDesktop ? '14px 44px 14px 16px' : '8px 44px 8px 12px');
  const inputFontSize = isCompact ? 16 : (isDesktop ? 20 : 16);
  const inputWidth = isCompact ? (isDesktop ? '400px' : '300px') : (isDesktop ? 650 : '95vw');
  const maxWidth = isCompact ? (isDesktop ? '400px' : '300px') : (isDesktop ? 650 : '95vw');
  
  return (
    <div className="search-wrapper" style={{ 
      width: isCompact ? 'auto' : '100%', 
      maxWidth: isCompact ? 'none' : maxWidth, 
      margin: '0 auto' 
    }}>
      <div style={{ 
        position: 'relative', 
        width: isCompact ? 'auto' : '100%', 
        display: isCompact ? 'block' : 'flex', 
        justifyContent: isCompact ? 'flex-start' : 'center' 
      }}>
        <input
          type="text"
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyDown={e => e.key === 'Enter' && onSearch()}
          style={{
            width: inputWidth,
            maxWidth: maxWidth,
            padding: inputPadding,
            borderRadius: 12,
            border: '1.5px solid #bbb',
            fontSize: inputFontSize,
            outline: 'none',
            boxSizing: 'border-box',
            background: '#fafafa',
            boxShadow: '0 1px 4px rgba(0,0,0,0.03)',
            margin: isCompact ? '0' : '0 auto',
            display: 'block'
          }}
          disabled={loading}
        />
        {/* Current Location Button */}
        {onCurrentLocation && (
          <button
            onClick={onCurrentLocation}
            style={{
              position: 'absolute',
              right: isCompact ? (isDesktop ? 36 : 36) : (isDesktop ? 50 : 36),
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 4,
              borderRadius: 4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#1D665D',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            title="Use current location"
            aria-label="Use current location"
          >
            <svg width={isCompact && !isDesktop ? "14" : "16"} height={isCompact && !isDesktop ? "14" : "16"} viewBox="0 0 24 24" fill="none" stroke="#1D665D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3"/>
              <path d="M12 1v6m0 6v6m11-7h-6m-6 0H1"/>
            </svg>
          </button>
        )}
        <span className="search-icon" onClick={onSearch} style={{ position: 'absolute', right: isCompact ? (isDesktop ? 8 : 16) : (isDesktop ? 18 : 8), top: '50%', transform: 'translateY(-50%)', cursor: 'pointer' }}>
                      <svg width={isCompact ? (isDesktop ? '18' : '16') : (isDesktop ? '22' : '18')} height={isCompact ? (isDesktop ? '18' : '16') : (isDesktop ? '22' : '18')} viewBox="0 0 18 18" fill="none">
            <circle cx="8" cy="8" r="7" stroke="#555" strokeWidth="2" />
            <line x1="13" y1="13" x2="17" y2="17" stroke="#555" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </span>
        {/* Autocomplete Dropdown */}
        {showDropdown && (
          <div style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: 'calc(100% + 4px)',
            background: '#fff',
            border: '1px solid #ddd',
            borderRadius: '12px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
            zIndex: 100,
            maxHeight: 320,
            overflowY: 'auto',
          }}>
            {/* Current Location Option */}
            {onCurrentLocation && (
              <div
                onMouseDown={() => {
                  onCurrentLocation();
                  // Close dropdown after selection
                  if (onBlur) {
                    setTimeout(() => onBlur(), 100);
                  }
                }}
                style={{
                  padding: '12px 16px',
                  cursor: 'pointer',
                  borderBottom: '1px solid #f0f0f0',
                  background: '#fff',
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  transition: 'background 0.12s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#f6f6f6'}
                onMouseLeave={e => e.currentTarget.style.background = '#fff'}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1D665D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="3"/>
                    <path d="M12 1v6m0 6v6m11-7h-6m-6 0H1"/>
                  </svg>
                  <span style={{ fontWeight: 700, color: '#1D665D', fontSize: 16 }}>Current Location</span>
                </div>
                <span style={{ color: '#888', fontSize: 13 }}>Use GPS</span>
              </div>
            )}
            
            {/* City/State Results */}
            {cityResults.length > 0 && cityResults.map((result, idx) => (
              <div
                key={result.cityState || result.label || idx}
                onMouseDown={() => onSelectResult(result)}
                style={{
                  padding: '12px 16px',
                  cursor: 'pointer',
                  borderBottom: idx !== cityResults.length - 1 ? '1px solid #f0f0f0' : 'none',
                  background: '#fff',
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  transition: 'background 0.12s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#f6f6f6'}
                onMouseLeave={e => e.currentTarget.style.background = '#fff'}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                    <circle cx="12" cy="10" r="3"/>
                  </svg>
                  <span style={{ fontWeight: 700, color: '#111', fontSize: 16 }}>{result.cityState || result.label || result}</span>
                </div>
                {result.count !== undefined && (
                  <span style={{ color: '#888', fontSize: 13, marginLeft: 12 }}>{result.count} course{result.count > 1 ? 's' : ''}</span>
                )}
              </div>
            ))}
            
            {/* Course Results */}
            {courseResults.length > 0 && (
              <>
                {/* Separator if both city and course results exist */}
                {cityResults.length > 0 && (
                  <div style={{
                    padding: '8px 16px',
                    background: '#f8f8f8',
                    borderTop: '1px solid #eee',
                    borderBottom: '1px solid #eee',
                    fontSize: 12,
                    color: '#666',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Golf Courses
                  </div>
                )}
                
                {courseResults.map((result, idx) => (
                  <div
                    key={result.courseId || result.name || idx}
                    onMouseDown={() => onSelectResult(result)}
                    style={{
                      padding: '12px 16px',
                      cursor: 'pointer',
                      borderBottom: idx !== courseResults.length - 1 ? '1px solid #f0f0f0' : 'none',
                      background: '#fff',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'background 0.12s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f6f6f6'}
                    onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 3v18h18"/>
                        <path d="M9 9h6v6H9z"/>
                        <path d="M9 3v6"/>
                        <path d="M15 3v6"/>
                        <path d="M9 21v-6"/>
                        <path d="M15 21v-6"/>
                      </svg>
                      <span style={{ fontWeight: 700, color: '#111', fontSize: 16 }}>{result.name}</span>
                    </div>
                    <div style={{ marginLeft: 24, fontSize: 14, color: '#666' }}>
                      at {result.clubName} • {result.city}, {result.state}
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default LocationSearchBar; 